import type { RouteResult, SignalControl } from '../shared/contracts.js';
import { distanceKm } from '../shared/return-routing.js';

/** Traffic lights show from street level in; a ping shows them at any zoom. */
export const SIGNAL_MIN_ZOOM = 14;
export const SIGNAL_PING_MS = 4000;

const SIGNAL_TITLES: Record<SignalControl, string> = {
  SIGNAL_THREE: 'Traffic lights · 3-way junction',
  SIGNAL_FOUR: 'Traffic lights · 4-way junction',
  SIGNAL_LEFT: 'Left-turn traffic lights',
  SIGNAL_OTHER: 'Traffic lights · crossing or other'
};

export interface SignalMarker {
  lat: number;
  lng: number;
  title: string;
}

export function signalMarkers(route: RouteResult): SignalMarker[] {
  const line = route.geometry.coordinates;
  return (route.features?.signals ?? []).flatMap(({ index, control }) => {
    const point = line[index];
    return point ? [{ lat: point[1], lng: point[0], title: SIGNAL_TITLES[control] }] : [];
  });
}

/** Speed bands for the route colouring, slowest first; null is a stretch with no mapped limit. */
export const SPEED_BANDS = [
  { label: '≤ 30', below: 35, color: '#7b3294' },
  { label: '40–50', below: 55, color: '#2b7bba' },
  { label: '60–70', below: 75, color: '#2e9e55' },
  { label: '80–90', below: 95, color: '#d99a00' },
  { label: '100+', below: Infinity, color: '#d7301f' }
] as const;
export const UNKNOWN_SPEED_COLOR = '#8d98a5';

export function speedColor(kmh: number | null): string {
  return kmh === null ? UNKNOWN_SPEED_COLOR : SPEED_BANDS.find((band) => kmh < band.below)!.color;
}

export interface SpeedStretch {
  path: Array<{ lat: number; lng: number }>;
  kmh: number | null;
  meters: number;
}

export function speedStretches(route: RouteResult): SpeedStretch[] {
  const line = route.geometry.coordinates;
  return (route.features?.speedLimits ?? []).flatMap(([from, to, kmh]) => {
    const path = line.slice(from, to + 1).map(([lng, lat]) => ({ lat, lng }));
    if (path.length < 2) return [];
    const meters = path.slice(1).reduce((sum, point, at) => sum + distanceKm(path[at]!, point) * 1000, 0);
    return [{ path, kmh, meters }];
  });
}

export function describeStretch({ kmh, meters }: SpeedStretch): string {
  const length = meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
  return `${kmh === null ? 'No mapped speed limit' : `${kmh} km/h`} · ${length}`;
}
